import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IRoutingService, RouteOptions, RoutePoint, RouteResult } from '../routing.types';

type ComputeRoutesResponse = {
  routes?: Array<{
    distanceMeters?: number;
    duration?: string;
    polyline?: { encodedPolyline?: string };
    legs?: Array<{ distanceMeters?: number; duration?: string }>;
  }>;
};

@Injectable({ providedIn: 'root' })
export class RoutesApiRoutingService implements IRoutingService {
  private readonly endpoint = 'https://routes.googleapis.com/directions/v2:computeRoutes';
  private cache: Map<string, RouteResult> = new Map();
  private readonly CACHE_PREFIX = 'routes_api_cache_';
  private readonly CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

  constructor(private readonly http: HttpClient) {
    this.loadCacheFromStorage();
  }

  async computeRoute(origin: RoutePoint, destination: RoutePoint, options: RouteOptions = {}): Promise<RouteResult> {
    const cacheKey = this.generateCacheKey(origin, destination, options);

    // Check memory cache first
    const cachedResult = this.cache.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    // Check localStorage cache
    const storedCache = this.getFromStorage(cacheKey);
    if (storedCache) {
      this.cache.set(cacheKey, storedCache);
      return storedCache;
    }

    const headers = new HttpHeaders({
      'X-Goog-Api-Key': environment.mapApi,
      'X-Goog-FieldMask': 'routes.distanceMeters,routes.duration,routes.polyline.encodedPolyline,routes.legs',
      'Content-Type': 'application/json',
    });

    const body: {
      origin: unknown;
      destination: unknown;
      travelMode: string | undefined;
      routingPreference: string | undefined;
      computeAlternativeRoutes: boolean;
      units: string | undefined;
      languageCode: string | undefined;
      routeModifiers: { avoidTolls: boolean; avoidHighways: boolean; avoidFerries: boolean };
      intermediates?: unknown[];
      departureTime?: string;
    } = {
      origin: { location: { latLng: { latitude: origin.lat, longitude: origin.lng } } },
      destination: { location: { latLng: { latitude: destination.lat, longitude: destination.lng } } },
      travelMode: options.travelMode || 'DRIVE',
      routingPreference: options.routingPreference || 'TRAFFIC_AWARE',
      computeAlternativeRoutes: !!options.alternatives,
      units: options.units || 'METRIC',
      languageCode: options.languageCode || 'es',
      routeModifiers: {
        avoidTolls: !!options.avoidTolls,
        avoidHighways: !!options.avoidHighways,
        avoidFerries: !!options.avoidFerries,
      },
    };

    if (options.waypoints?.length) {
      body.intermediates = options.waypoints.map((w) => ({
        location: { latLng: { latitude: w.lat, longitude: w.lng } },
      }));
    }
    if (options.departureTime) {
      body.departureTime =
        options.departureTime === 'now' ? new Date().toISOString() : (options.departureTime as Date).toISOString();
    }

    const resp = await lastValueFrom<ComputeRoutesResponse>(this.http.post(this.endpoint, body, { headers }));
    const route = resp?.routes?.[0];
    if (!route) throw new Error('No route returned from Routes API');

    const durationSeconds = parseISODurationToSeconds(route.duration);
    const result: RouteResult = {
      encodedPolyline: route.polyline?.encodedPolyline || '',
      distanceMeters: route.distanceMeters || 0,
      durationSeconds,
      legs: (route.legs || []).map((l) => ({
        distanceMeters: l.distanceMeters || 0,
        duration: parseISODurationToSeconds(l.duration),
      })),
      provider: 'routes',
    };

    // Cache the result
    this.cache.set(cacheKey, result);
    this.saveToStorage(cacheKey, result);

    return result;
  }

  /**
   * Clear cache for a specific route
   */
  clearRouteCache(routeId: string | number): void {
    const cacheKey = `${this.CACHE_PREFIX}route_${routeId}`;
    this.cache.delete(cacheKey);
    localStorage.removeItem(cacheKey);
  }

  /**
   * Clear all cached routes
   */
  clearAllCache(): void {
    this.cache.clear();
    // Clear all routes cache from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(this.CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Generate cache key from request
   */
  private generateCacheKey(origin: RoutePoint, destination: RoutePoint, options: RouteOptions): string {
    const originStr = `${origin.lat},${origin.lng}`;
    const destinationStr = `${destination.lat},${destination.lng}`;
    const waypointsStr = options.waypoints?.map((w) => `${w.lat},${w.lng}`).join('|') || '';
    const travelMode = options.travelMode || 'DRIVE';
    const routingPreference = options.routingPreference || 'TRAFFIC_AWARE';
    const avoidTolls = options.avoidTolls ? '1' : '0';
    const avoidHighways = options.avoidHighways ? '1' : '0';
    const avoidFerries = options.avoidFerries ? '1' : '0';
    const units = options.units || 'METRIC';
    const languageCode = options.languageCode || 'es';

    return `${this.CACHE_PREFIX}${originStr}_${destinationStr}_${waypointsStr}_${travelMode}_${routingPreference}_${avoidTolls}_${avoidHighways}_${avoidFerries}_${units}_${languageCode}`;
  }

  /**
   * Save to localStorage with expiry
   */
  private saveToStorage(key: string, data: RouteResult): void {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        expiry: Date.now() + this.CACHE_EXPIRY_MS,
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save routes cache to localStorage:', error);
    }
  }

  /**
   * Get from localStorage with expiry check
   */
  private getFromStorage(key: string): RouteResult | null {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const cacheData = JSON.parse(stored);

      // Check if cache has expired
      if (Date.now() > cacheData.expiry) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.warn('Failed to load routes cache from localStorage:', error);
      return null;
    }
  }

  /**
   * Load cache from localStorage on service initialization
   */
  private loadCacheFromStorage(): void {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          const cached = this.getFromStorage(key);
          if (cached) {
            this.cache.set(key, cached);
          }
        }
      });
    } catch (error) {
      console.warn('Failed to load routes cache from localStorage:', error);
    }
  }
}

function parseISODurationToSeconds(value: string | undefined): number {
  if (!value) return 0;
  const direct = value.match(/^(\d+)s$/);
  if (direct) return parseInt(direct[1], 10);
  const m = value.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
  if (!m) return 0;
  const h = Number(m[1] || 0),
    min = Number(m[2] || 0),
    s = Number(m[3] || 0);
  return h * 3600 + min * 60 + s;
}
